import { IBenefitSelectionRule } from "types/Account/Account";

/**
 * CLE-2792 — TODAY'S EFFECTIVE PRECEDENCE, written into the array order before it is saved.
 *
 * 🚨 THIS EXISTS BECAUSE THE API CHANGED WHAT AN ARRAY MEANS. `POST /accounts/benefitSelectionRules`
 * now writes `priority = index` and the engine resolves by stored priority, first match wins. The
 * old rule was not an order at all: the most SPECIFIC rule won (payers named beats plans named beats
 * neither), oldest breaking a tie. So the account page saving the list in the order it happens to
 * display would silently re-rank every account whose rules were not already in specificity order —
 * a broad "all payers" rule created first would land at index 0 and start winning over the narrow
 * one written for one payer, changing what patients are quoted with nothing on screen having moved.
 *
 * 🚨 IT MIRRORS `rulesInSpecificityOrder` IN `benefitSelectionRules.ts` ON THE API, DELIBERATELY, and
 * the two must agree: that one is the definition, kept alive to backfill stored priorities, and this
 * one keeps the untouched save path producing the same ranking the backfill produced. If the scoring
 * changes there, change it here.
 *
 * ⚠️ ONLY THE NON-GATED PATH USES THIS. The benefit-configuration card lets a person ORDER the list
 * by hand, so the array it sends is a decision and must never be re-sorted underneath them.
 */
const specificity = (rule: IBenefitSelectionRule): number =>
  ((rule.payerIds ?? []).length > 0 ? 2 : 0) +
  ((rule.planKeywords ?? []).length > 0 ? 1 : 0);

export const rulesInSpecificityOrder = <T extends IBenefitSelectionRule>(
  rules: T[]
): T[] =>
  [...(rules ?? [])]
    // The loaded index is the last tie-break: the account GET returns these `createdAt asc`, so a
    // row with no `createdAt` of its own (one added on this screen) keeps the position it was given.
    .map((rule, index) => ({ rule, index }))
    .sort((a, b) => {
      const byScore = specificity(b.rule) - specificity(a.rule);
      if (byScore !== 0) return byScore;
      const aCreated = a.rule.createdAt ? Date.parse(a.rule.createdAt) : NaN;
      const bCreated = b.rule.createdAt ? Date.parse(b.rule.createdAt) : NaN;
      if (!Number.isNaN(aCreated) && !Number.isNaN(bCreated)) {
        if (aCreated !== bCreated) return aCreated - bCreated;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.rule);
